import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ApiUrl from "../Environment/ApiUrl";
import { useToast } from "../Toast/ToastContext";

function AddPurchase({ toggleSidebar }) {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [generateNumber, setGenerateNumber] = useState("");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPaymentHistory, setSupplierPaymentHistory] = useState([]);
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [returnPureCalculated, setReturnPureCalculated] = useState("0.000");
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [generateSupplierNumber, setGenerateSupplierNumber] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [isTranslatingSupplier, setIsTranslatingSupplier] = useState(false);

  // Supplier form data with Tamil name
  const [supplierFormData, setSupplierFormData] = useState({
    supplier_id: "",
    name: "",
    name_tamil: "",
    mobile: "",
    address: "",
    status: "Active",
  });

  // Product form data with Tamil name
  const [productAddData, setProductAddData] = useState({
    item_code: "",
    item_name: "",
    item_name_english: "",
    item_name_tamil: "",
    category: "",
    type: "",
    pure_percent: "",
    status: "Active",
    image: null,
  });

  const [showProductModal, setShowProductModal] = useState(false);
  const [productFormErrors, setProductFormErrors] = useState({});
  const [supplierFormErrors, setSupplierFormErrors] = useState({});

  const [todayRates, setTodayRates] = useState({
    gold_pure_rate: 0,
    silver_pure_rate: 0,
    date: "",
  });

  const [formData, setFormData] = useState({
    purchase_id: "",
    purchase_date: new Date().toISOString().split("T")[0],
    supplier_id: "",
    return_type: "",
    paid: "",
    pure: "",
    opening: "0.000",
    current: "0.000",
    close: "0.000",
    total_pure: "0.000",
    enter_amount: "",
    items: [],
    supplier_details: {
      supplier_id: "",
      name: null,
      name_tamil: null,
      mobile: null,
      address: null,
    },
  });

  const [newItem, setNewItem] = useState({
    item: "",
    category: "",
    other_name: "",
    item_name: "",
    item_name_tamil: "",
    type: "",
    quantity: 1,
    weight: "",
    purity: "",
    pure: "0.000",
    total_amount: "0.000",
    adjustment: "0.000",
  });

  const [adjustmentInput, setAdjustmentInput] = useState("");

  // ─── Translation Functions ──────────────────────────────────────────────────

  // Translate to Tamil (for products)
  const translateToTamil = async (englishText) => {
    if (!englishText || englishText.trim().length < 2) return "";
    setIsTranslating(true);

    const apis = [
      async () => {
        const encoded = encodeURIComponent(englishText.trim());
        const res = await fetch(
          `https://api.mymemory.translated.net/get?q=${encoded}&langpair=en|ta`
        );
        const data = await res.json();
        const translated = data?.responseData?.translatedText;
        if (translated && translated.toLowerCase() !== englishText.toLowerCase()) {
          return translated;
        }
        return null;
      },
      async () => {
        const encoded = encodeURIComponent(englishText.trim());
        const res = await fetch(`https://lingva.ml/api/v1/en/ta/${encoded}`);
        if (res.ok) {
          const data = await res.json();
          if (data.translation && data.translation !== englishText) {
            return data.translation;
          }
        }
        return null;
      },
      async () => {
        const res = await fetch("https://libretranslate.com/translate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            q: englishText.trim(),
            source: "en",
            target: "ta",
            format: "text",
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.translatedText) {
            return data.translatedText;
          }
        }
        return null;
      },
    ];

    for (const api of apis) {
      try {
        const result = await api();
        if (result) {
          setIsTranslating(false);
          return result;
        }
      } catch (error) {
        console.warn("Translation API failed:", error);
        continue;
      }
    }

    setIsTranslating(false);
    return "";
  };

  // Translate supplier name to Tamil
  const translateSupplierToTamil = async (englishText) => {
    if (!englishText || englishText.trim().length < 2) return "";
    setIsTranslatingSupplier(true);

    try {
      const encoded = encodeURIComponent(englishText.trim());
      const res = await fetch(
        `https://api.mymemory.translated.net/get?q=${encoded}&langpair=en|ta`
      );
      const data = await res.json();
      const translated = data?.responseData?.translatedText;
      if (translated && translated.toLowerCase() !== englishText.toLowerCase()) {
        setIsTranslatingSupplier(false);
        return translated;
      }
    } catch (error) {
      console.warn("Supplier translation failed:", error);
    }

    // Try Lingva as fallback
    try {
      const encoded = encodeURIComponent(englishText.trim());
      const res = await fetch(`https://lingva.ml/api/v1/en/ta/${encoded}`);
      if (res.ok) {
        const data = await res.json();
        if (data.translation && data.translation !== englishText) {
          setIsTranslatingSupplier(false);
          return data.translation;
        }
      }
    } catch (error) {
      console.warn("Lingva supplier translation failed:", error);
    }

    setIsTranslatingSupplier(false);
    return "";
  };

  // ─── Product Functions ──────────────────────────────────────────────────────

  const fetchProductItemCode = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/product.php/generate-item-code`);
      setProductAddData((prev) => ({
        ...prev,
        item_code: response.data?.item_code || "",
      }));
    } catch (error) {
      console.error("Error fetching product item code:", error.message);
      toast.error("Failed to generate product code", 3000, "topRight");
    }
  };

  const openProductModal = () => {
    fetchProductItemCode();
    setProductAddData({
      item_code: "",
      item_name: "",
      item_name_english: "",
      item_name_tamil: "",
      category: "",
      type: "",
      pure_percent: "",
      status: "Active",
      image: null,
    });
    setProductFormErrors({});
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setProductAddData({
      item_code: "",
      item_name: "",
      item_name_english: "",
      item_name_tamil: "",
      category: "",
      type: "",
      pure_percent: "",
      status: "Active",
      image: null,
    });
    setProductFormErrors({});
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!productAddData.item_name_english) errors.item_name = "Item name is required";
    if (!productAddData.category) errors.category = "Category is required";
    if (!productAddData.pure_percent) errors.pure_percent = "Purity percentage is required";
    if (!productAddData.status) errors.status = "Status is required";

    if (Object.keys(errors).length > 0) {
      setProductFormErrors(errors);
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      let finalTamilName = productAddData.item_name_tamil;
      if (!finalTamilName && productAddData.item_name_english) {
        finalTamilName = await translateToTamil(productAddData.item_name_english);
      }

      formData.append("item_code", productAddData.item_code || "");
      formData.append("item_name", productAddData.item_name_english || "");
      formData.append("item_name_english", productAddData.item_name_english || "");
      formData.append("item_name_tamil", finalTamilName || "");
      formData.append("category", productAddData.category || "");
      formData.append("type", productAddData.type || "");
      formData.append("pure_percent", productAddData.pure_percent || "");
      formData.append("status", productAddData.status || "");
      formData.append("created_by", "purchase");

      if (productAddData.image) {
        formData.append("image", productAddData.image);
      }

      const response = await axios.post(`${ApiUrl}/Api/product.php`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data?.status === true || response.status === 200 || response.status === 201) {
        toast.success("Product added successfully!", 3000, "topRight");
        closeProductModal();
        await fetchProducts();
      } else {
        throw new Error(response.data?.message || "Failed to add product");
      }
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error(error.response?.data?.message || "Failed to add product", 3000, "topRight");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/product.php`);
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch products", 3000, "topRight");
    }
  };

  // ─── Supplier Functions ─────────────────────────────────────────────────────

  const openSupplierModal = async () => {
    try {
      const response = await axios.get(
        `${ApiUrl}/Api/supplier.php/generate-supplier-code`
      );
      const newId = response.data?.supplier_id || "";
      setGenerateSupplierNumber(newId);
      setSupplierFormData((prev) => ({ 
        ...prev, 
        supplier_id: newId,
        name_tamil: "" 
      }));
      setShowSupplierModal(true);
    } catch (error) {
      toast.error("Failed to generate supplier ID", 3000, "topRight");
    }
  };

  const closeSupplierModal = () => {
    setShowSupplierModal(false);
    setSupplierFormData({
      supplier_id: "",
      name: "",
      name_tamil: "",
      mobile: "",
      address: "",
      status: "Active",
    });
    setGenerateSupplierNumber("");
    setSupplierFormErrors({});
  };

  const validateSupplierForm = (data) => {
    const errors = {};
    if (!data.supplier_id) errors.supplier_id = "Supplier ID is required";
    if (!data.name) errors.name = "Name is required";
    if (!data.mobile) {
      errors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(data.mobile)) {
      errors.mobile = "Mobile number must be 10 digits";
    }
    if (!data.address) errors.address = "Address is required";
    if (!data.status) errors.status = "Status is required";
    setSupplierFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    if (!validateSupplierForm(supplierFormData)) return;

    try {
      setLoading(true);

      // Auto-translate if Tamil name is empty
      let finalTamilName = supplierFormData.name_tamil;
      if (!finalTamilName && supplierFormData.name) {
        finalTamilName = await translateSupplierToTamil(supplierFormData.name);
      }

      const currentDateTime = new Date()
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");
      const payload = {
        ...supplierFormData,
        name_tamil: finalTamilName || "",
        created_by: "admin",
        created_date: currentDateTime,
        updated_by: "admin",
        updated_date: currentDateTime,
      };

      console.log("Sending supplier data:", payload);

      const addResponse = await axios.post(
        `${ApiUrl}/Api/supplier.php`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Add supplier response:", addResponse.data);

      if (addResponse.data?.status === false) {
        throw new Error(addResponse.data?.message || "Failed to add supplier");
      }

      toast.success("Supplier added successfully!", 3000, "topRight");

      // Refresh suppliers list
      const response = await axios.get(`${ApiUrl}/Api/supplier.php`);
      setSuppliers(response.data?.data || []);

      // Auto-select the newly added supplier
      const newSupplier = response.data?.data?.find(
        (s) => s.supplier_id === payload.supplier_id
      );
      if (newSupplier) {
        handleSelectSupplier(newSupplier);
      }

      closeSupplierModal();
    } catch (error) {
      console.error("Error adding supplier:", error);
      console.error("Error response:", error.response?.data);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to add supplier";
      toast.error(errorMessage, 3000, "topRight");
    } finally {
      setLoading(false);
    }
  };

  // ─── Data Fetching ──────────────────────────────────────────────────────────

  const fetchTodayRates = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/daily_rates.php/current`);
      if (response.data.status && response.data.data) {
        const rates = response.data.data;
        setTodayRates({
          gold_pure_rate: parseFloat(rates.gold_pure_percentage) || 0,
          silver_pure_rate: parseFloat(rates.silver_pure_percentage) || 0,
          date: rates.date,
        });
      }
    } catch (error) {
      console.error("Failed to fetch today's rates", error);
    }
  };

  // ─── Effects ────────────────────────────────────────────────────────────────

  // Filter products based on selected category
  useEffect(() => {
    if (newItem.category) {
      const filtered = products.filter(product => 
        product.category === newItem.category
      );
      setFilteredProducts(filtered);
      setNewItem(prev => ({ ...prev, item_name: "", item_name_tamil: "" }));
    } else {
      setFilteredProducts([]);
    }
  }, [newItem.category, products]);

  // Calculate pure and total_amount for new item
  useEffect(() => {
    let pure = "0.000";
    let total_amount = "0.000";

    if (newItem.weight && newItem.purity && newItem.quantity) {
      const weight = parseFloat(newItem.weight) || 0;
      const purity = parseFloat(newItem.purity) || 0;
      const quantity = parseFloat(newItem.quantity) || 1;

      if (purity > 100) {
        toast.error("Purity cannot exceed 100%", 3000, "topRight");
        setNewItem((prev) => ({ ...prev, purity: "" }));
        return;
      }

      const singlePure = (weight * purity) / 100;
      let calculatedPure = quantity * singlePure;
      const adjustment = parseFloat(newItem.adjustment) || 0;
      calculatedPure += adjustment;

      pure = calculatedPure.toFixed(3);
      total_amount = (quantity * weight).toFixed(3);
    }

    setNewItem((prev) => ({ ...prev, pure, total_amount }));
  }, [newItem.weight, newItem.purity, newItem.quantity, newItem.adjustment, toast]);

  // Calculate total_pure from items
  useEffect(() => {
    const totalPure = formData.items
      .reduce((sum, item) => sum + parseFloat(item.pure || 0), 0)
      .toFixed(3);
    setFormData((prev) => ({ ...prev, total_pure: totalPure }));
  }, [formData.items]);

  // Main calculation: Opening → Current → Closing
  useEffect(() => {
    const opening = parseFloat(formData.opening) || 0;
    const totalPure = parseFloat(formData.total_pure) || 0;
    const current = (opening + totalPure).toFixed(3);

    let returnedPureGrams = 0;

    if (formData.return_type === "Pure") {
      returnedPureGrams = parseFloat(formData.pure) || 0;
    } else if (formData.return_type === "Amount") {
      const paid = parseFloat(formData.paid) || 0;
      if (paid > 0) {
        const hasGold = formData.items.some((i) => (i.category === "Gold" || i.category === "Gold Coin"));
        const hasSilver = formData.items.some((i) => (i.category === "Silver" || i.category === "Silver Coin"));

        let rate = 0;
        if (hasGold && !hasSilver) {
          rate = todayRates.gold_pure_rate;
        } else if (hasSilver && !hasGold) {
          rate = todayRates.silver_pure_rate;
        }

        returnedPureGrams = rate > 0 ? paid / rate : 0;
        setReturnPureCalculated(returnedPureGrams.toFixed(3));
      }
    }

    const closing = Math.max(0, parseFloat(current) - returnedPureGrams).toFixed(3);

    setFormData((prev) => ({
      ...prev,
      current,
      close: closing,
    }));
  }, [
    formData.opening,
    formData.total_pure,
    formData.pure,
    formData.paid,
    formData.return_type,
    formData.items,
    todayRates.gold_pure_rate,
    todayRates.silver_pure_rate,
  ]);

  // Calculate enter_amount (₹ value of the purchased items) — this is the
  // purchase's total value regardless of how it was returned (Pure grams
  // or Amount/cash), so it should never be blank/zero just because
  // return_type isn't "Amount".
  useEffect(() => {
    if (formData.items.length === 0) {
      setFormData((prev) => ({ ...prev, enter_amount: "" }));
      return;
    }

    let goldPure = 0, silverPure = 0;
    formData.items.forEach((item) => {
      const pure = parseFloat(item.pure) || 0;
      if ((item.category === "Gold" || item.category === "Gold Coin")) goldPure += pure;
      if ((item.category === "Silver" || item.category === "Silver Coin")) silverPure += pure;
    });

    const goldValue = goldPure * todayRates.gold_pure_rate;
    const silverValue = silverPure * todayRates.silver_pure_rate;
    const total = (goldValue + silverValue).toFixed(2);

    setFormData((prev) => ({ ...prev, enter_amount: total }));
  }, [formData.items, todayRates]);

  // Reset type when item is "Bar"
  useEffect(() => {
    if (newItem.item === "Bar") {
      setNewItem((prev) => ({ ...prev, type: "", purity: "100" }));
    }
  }, [newItem.item]);

  // Fetch initial data
  useEffect(() => {
    toggleSidebar();
    fetchTodayRates();
    fetchInitialData();
    fetchProducts();
    if (purchaseId) fetchPurchaseData(purchaseId);
  }, [purchaseId]);

  const fetchInitialData = async () => {
    try {
      const idResponse = await axios.get(
        `${ApiUrl}/Api/purchase_details.php/generate-purchase-code`
      );
      const newId = idResponse.data.purchase_id || "";
      setGenerateNumber(newId);
      if (!purchaseId) {
        setFormData((prev) => ({ ...prev, purchase_id: newId }));
      }

      const [supplierRes, typeRes] = await Promise.all([
        axios.get(`${ApiUrl}/Api/supplier.php`),
        axios.get(`${ApiUrl}/Api/product_type.php`),
      ]);

      setSuppliers(supplierRes.data?.data || []);
      setProductTypes(typeRes.data?.data || []);
    } catch (error) {
      toast.error("Failed to load initial data", 3000, "topRight");
    }
  };

  const fetchPurchaseData = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${ApiUrl}/Api/purchase_details.php?id=${id}`
      );
      const data = response.data.data[0];
      if (data) {
        setFormData({
          purchase_id: data.purchase_id,
          purchase_date: data.purchase_date,
          supplier_id: data.supplier_id,
          return_type: data.return_type,
          paid: data.paid || "",
          pure: data.pure || "",
          opening: parseFloat(data.opening).toFixed(3),
          current: parseFloat(data.current).toFixed(3),
          close: parseFloat(data.close).toFixed(3),
          total_pure: parseFloat(data.total_pure).toFixed(3),
          enter_amount: data.enter_amount || "",
          items: data.items.map((item, i) => ({
            id: i + 1,
            ...item,
            quantity: parseFloat(item.quantity) || 0,
            weight: parseFloat(item.weight) || 0,
            purity: parseFloat(item.percentage) || 0,
            pure: parseFloat(item.pure) || 0,
            adjustment: parseFloat(item.adjustment || 0).toFixed(3),
          })),
          supplier_details: { ...data.supplier },
        });
        setSelectedSupplier(data.supplier);
        setSearchTerm(`${data.supplier.supplier_id} - ${data.supplier.name}`);
        setGenerateNumber(data.purchase_id);
      }
    } catch (error) {
      toast.error("Failed to load purchase", 3000, "topRight");
    } finally {
      setLoading(false);
    }
  };

  // Opening balance logic
  useEffect(() => {
    if (!selectedSupplier?.supplier_id || formData.items.length === 0) {
      setFormData((prev) => ({ ...prev, opening: "0.000" }));
      return;
    }

    const fetchOpening = async () => {
      try {
        const res = await axios.get(`${ApiUrl}/Api/purchase_details.php`);
        const purchases = res.data?.data || [];

        const currentGold = formData.items.some((i) => (i.category === "Gold" || i.category === "Gold Coin"));
        const currentSilver = formData.items.some(
          (i) => (i.category === "Silver" || i.category === "Silver Coin")
        );

        if (!currentGold && !currentSilver) {
          setFormData((prev) => ({ ...prev, opening: "0.000" }));
          return;
        }

        const supplierPurchases = purchases
          .filter((p) => p.supplier_id === selectedSupplier.supplier_id)
          .sort(
            (a, b) => new Date(b.purchase_date) - new Date(a.purchase_date)
          );

        if (supplierPurchases.length === 0) {
          setFormData((prev) => ({ ...prev, opening: "0.000" }));
          return;
        }

        const latestMatching = supplierPurchases.find((purchase) => {
          const hasGold = purchase.items?.some((i) => (i.category === "Gold" || i.category === "Gold Coin"));
          const hasSilver = purchase.items?.some(
            (i) => (i.category === "Silver" || i.category === "Silver Coin")
          );

          if (currentGold && !currentSilver) return hasGold && !hasSilver;
          if (currentSilver && !currentGold) return hasSilver && !hasGold;
          return false;
        });

        const opening = latestMatching
          ? parseFloat(latestMatching.close || 0).toFixed(3)
          : "0.000";

        setFormData((prev) => ({ ...prev, opening }));
      } catch (err) {
        console.error("Error fetching opening balance", err);
        setFormData((prev) => ({ ...prev, opening: "0.000" }));
      }
    };

    fetchOpening();
  }, [selectedSupplier?.supplier_id, formData.items]);

  // Fetch supplier payment history
  useEffect(() => {
    if (!selectedSupplier?.supplier_id) {
      setSupplierPaymentHistory([]);
      return;
    }

    const fetchPaymentHistory = async () => {
      setLoadingPaymentHistory(true);
      try {
        const res = await axios.get(`${ApiUrl}/Api/purchase_details.php`);
        const purchases = res.data?.data || [];

        const history = purchases
          .filter((p) => p.supplier_id === selectedSupplier.supplier_id)
          .sort(
            (a, b) => new Date(b.purchase_date) - new Date(a.purchase_date)
          );

        setSupplierPaymentHistory(history);
      } catch (err) {
        console.error("Error fetching supplier payment history", err);
        setSupplierPaymentHistory([]);
      } finally {
        setLoadingPaymentHistory(false);
      }
    };

    fetchPaymentHistory();
  }, [selectedSupplier?.supplier_id]);

  // ─── Handler Functions ─────────────────────────────────────────────────────

  const handleSelectSupplier = async (supplier) => {
    setSelectedSupplier(supplier);
    setSearchTerm(`${supplier.supplier_id} - ${supplier.name}`);
    setFormData((prev) => ({
      ...prev,
      supplier_id: supplier.supplier_id,
      supplier_details: { ...supplier },
      opening: "0.000",
    }));
    setShowDropdown(false);
  };

  const clearSupplier = () => {
    setSelectedSupplier(null);
    setSearchTerm("");
    setFormData((prev) => ({
      ...prev,
      supplier_id: "",
      supplier_details: {
        supplier_id: "",
        name: null,
        name_tamil: null,
        mobile: null,
        address: null,
      },
      opening: "0.000",
    }));
  };

  const validateItem = () => {
    if (!newItem.item) return toast.error("Select item", 3000, "topRight");
    if (!newItem.category) return toast.error("Select category", 3000, "topRight");
    if (!newItem.quantity || newItem.quantity <= 0) return toast.error("Invalid quantity", 3000, "topRight");
    if (!newItem.weight || newItem.weight <= 0) return toast.error("Invalid weight", 3000, "topRight");
    if (!newItem.purity || newItem.purity <= 0 || newItem.purity > 100) return toast.error("Purity 0-100%", 3000, "topRight");
    if (newItem.item === "Others" && !newItem.other_name) return toast.error("Enter others name", 3000, "topRight");
    return true;
  };

  const handleAddItem = () => {
    if (!validateItem()) return;
    const items = [
      ...formData.items,
      {
        id: formData.items.length + 1,
        purchase_id: formData.purchase_id,
        item: newItem.item,
        other_name: newItem.other_name,
        item_name: newItem.item_name,
        item_name_tamil: newItem.item_name_tamil || "",
        category: newItem.category,
        type: newItem.type || "",
        quantity: parseFloat(newItem.quantity),
        weight: parseFloat(newItem.weight),
        purity: parseFloat(newItem.purity),
        pure: parseFloat(newItem.pure),
        total_amount: parseFloat(newItem.total_amount),
        adjustment: parseFloat(newItem.adjustment || 0),
      },
    ];
    setFormData((prev) => ({ ...prev, items }));
    resetNewItem();
  };

  const resetNewItem = () => {
    setNewItem({
      item: "",
      category: "",
      other_name: "",
      item_name: "",
      item_name_tamil: "",
      type: "",
      quantity: 1,
      weight: "",
      purity: "",
      pure: "0.000",
      total_amount: "0.000",
      adjustment: "0.000",
    });
    setAdjustmentInput("");
    setEditingItemId(null);
  };

  const handleRemoveItem = (id) => {
    const items = formData.items
      .filter((i) => i.id !== id)
      .map((i, idx) => ({ ...i, id: idx + 1 }));
    setFormData((prev) => ({ ...prev, items }));
  };

  const handleEditItem = (id) => {
    const item = formData.items.find((i) => i.id === id);
    if (item) {
      setNewItem({
        ...item,
        quantity: item.quantity.toString(),
        weight: item.weight.toString(),
        purity: item.purity.toString(),
      });
      setEditingItemId(id);
    }
  };

  const handleUpdateItem = () => {
    if (!validateItem() || !editingItemId) return;
    const items = formData.items.map((i) =>
      i.id === editingItemId
        ? {
            ...i,
            item: newItem.item,
            category: newItem.category,
            other_name: newItem.other_name,
            item_name: newItem.item_name,
            item_name_tamil: newItem.item_name_tamil || "",
            type: newItem.type,
            quantity: parseFloat(newItem.quantity),
            weight: parseFloat(newItem.weight),
            purity: parseFloat(newItem.purity),
            pure: parseFloat(newItem.pure),
            adjustment: parseFloat(newItem.adjustment || 0),
          }
        : i,
    );
    setFormData((prev) => ({ ...prev, items }));
    resetNewItem();
  };

  const validateForm = () => {
    if (!formData.supplier_id) return toast.error("Select supplier", 3000, "topRight");
    if (formData.items.length === 0) return toast.error("Add at least one item", 3000, "topRight");
    if (!formData.return_type) return toast.error("Select return type", 3000, "topRight");
    if (formData.return_type === "Pure") {
      const pureReturn = parseFloat(formData.pure);
      const current = parseFloat(formData.current);

      if (!pureReturn || pureReturn <= 0) {
        return toast.error("Enter valid pure return", 3000, "topRight");
      }

      if (pureReturn > current) {
        return toast.error("Return Pure cannot exceed Current", 3000, "topRight");
      }
    }

    if (formData.return_type === "Amount") {
      const return_pure = parseFloat(formData.return_pure);
      const paid = parseFloat(formData.paid);
      const current = parseFloat(formData.current);
      const enterAmount = parseFloat(formData.enter_amount);

      if (!paid || paid <= 0) {
        return toast.error("Enter valid paid amount", 3000, "topRight");
      }

      const hasGold = formData.items.some((i) => (i.category === "Gold" || i.category === "Gold Coin"));
      const hasSilver = formData.items.some((i) => (i.category === "Silver" || i.category === "Silver Coin"));
      let rate = 0;

      if (hasGold && !hasSilver) {
        rate = todayRates.gold_pure_rate;
      } else if (hasSilver && !hasGold) {
        rate = todayRates.silver_pure_rate;
      }

      if (rate > 0) {
        const paidInGrams = paid / rate;
        if (paidInGrams > current) {
          return toast.error("Paid amount exceeds available current balance", 3000, "topRight");
        }
      }

      if (return_pure > current) {
        return toast.error("Paid amount cannot exceed Enter Amount", 3000, "topRight");
      }
    }
    return true;
  };

  const preparePayload = () => {
    const items = formData.items.map((i) => ({
      category: i.category,
      quantity: parseFloat(i.quantity) || 0,
      type: i.type || "",
      item: i.item,
      weight: parseFloat(i.weight) || 0,
      other_name: i.other_name || "",
      percentage: parseFloat(i.purity) || 0,
      pure: parseFloat(i.pure) || 0,
      adjustment: parseFloat(i.adjustment || 0),
    }));

    return {
      purchase_id: formData.purchase_id,
      purchase_date: formData.purchase_date,
      supplier_id: formData.supplier_id,
      return_type: formData.return_type,
      enter_amount: parseFloat(formData.enter_amount) || 0,
      paid: formData.return_type === "Amount" ? parseFloat(formData.paid) || 0 : 0,
      pure:
        formData.return_type === "Pure"
          ? parseFloat(formData.pure) || 0
          : formData.return_type === "Amount"
            ? parseFloat(returnPureCalculated) || 0
            : 0,
      opening: parseFloat(formData.opening) || 0,
      current: parseFloat(formData.current) || 0,
      close: parseFloat(formData.close) || 0,
      total_pure: parseFloat(formData.total_pure) || 0,
      items: items,
    };
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = preparePayload();
      console.log("Sending payload:", JSON.stringify(payload, null, 2));
      
      const response = await axios.post(`${ApiUrl}/Api/purchase_details.php`, payload);
      console.log("Response:", response.data);
      
      if (response.data && response.data.status === true) {
        toast.success("Purchase saved successfully!", 3000, "topRight");
        handleClear();
        navigate("/purchases");
      } else {
        toast.error(response.data?.message || "Save failed", 3000, "topRight");
      }
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      toast.error(err.response?.data?.message || "Save failed", 3000, "topRight");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      await axios.post(`${ApiUrl}/Api/purchase_details.php`, preparePayload());
      toast.success("Ready to print!", 3000, "topRight");
      setShowModal(true);
    } catch (err) {
      toast.error("Print failed", 3000, "topRight");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      ...formData,
      purchase_id: generateNumber,
      return_type: "",
      paid: "",
      pure: "",
      enter_amount: "",
      items: [],
      opening: "0.000",
      current: "0.000",
      close: "0.000",
      total_pure: "0.000",
    });
    clearSupplier();
    resetNewItem();
  };

  const closeModal = () => {
    setShowModal(false);
    navigate(`/purchase-bill/${formData.purchase_id}`);
  };

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message">
            <div className="loadinggif"></div>
          </div>
        </div>
      )}
      <div className="container-fluid py-2">
        {/* Form Header */}
        <div className="row">
          <div className="col-lg-3">
            <div className="card p-2">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Purchase ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={generateNumber}
                    readOnly
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.purchase_date}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        purchase_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-9">
            <div className="card p-2">
              <div className="row g-3">
                <div className="col-md-3 position-relative">
                  <label className="form-label">Supplier</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search supplier..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                      }}
                      onFocus={() => setShowDropdown(true)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        style={{ height: "35px" }}
                        onClick={clearSupplier}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                    {!selectedSupplier && (
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        style={{ height: "35px" }}
                        onClick={openSupplierModal}
                        title="Add New Supplier"
                      >
                        <i className="bi bi-plus"></i>
                      </button>
                    )}
                  </div>

                  {showDropdown && suppliers.length > 0 && (
                    <div className="dropdown-menu show w-100">
                      {suppliers
                        .filter(
                          (supplier) =>
                            supplier.supplier_id
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            supplier.name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            (supplier.name_tamil &&
                              supplier.name_tamil
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase()))
                        )
                        .map((supplier) => (
                          <button
                            key={supplier.supplier_id}
                            className="dropdown-item"
                            onClick={() => handleSelectSupplier(supplier)}
                            type="button"
                          >
                            {supplier.supplier_id} - {supplier.name}
                            {supplier.name_tamil && (
                              <span className="text-muted ms-1">
                                / {supplier.name_tamil}
                              </span>
                            )}
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                <div className="col-md-3">
                  <label className="form-label">Supplier Name</label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ opacity: 0.6, backgroundColor: "#f0f0f0" }}
                    value={
                      formData.supplier_details?.name_tamil
                        ? `${formData.supplier_details.name} / ${formData.supplier_details.name_tamil}`
                        : formData.supplier_details?.name ||
                          selectedSupplier?.name ||
                          ""
                    }
                    disabled
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Mobile</label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ opacity: 0.6, backgroundColor: "#f0f0f0" }}
                    value={
                      formData.supplier_details?.mobile ||
                      selectedSupplier?.mobile ||
                      ""
                    }
                    disabled
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ opacity: 0.6, backgroundColor: "#f0f0f0" }}
                    value={
                      formData.supplier_details?.address ||
                      selectedSupplier?.address ||
                      ""
                    }
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Item Entry Form */}
        <div className="card p-2 mt-1">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">Item</label>
              <select
                className="form-select"
                value={newItem.item}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    item: e.target.value,
                    type: e.target.value === "Bar" ? "" : newItem.type,
                  })
                }
              >
                <option value="">Select Item</option>
                <option value="Bar">Bar</option>
                <option value="Jewels">Jewels</option>
                <option value="Others">Others</option>
              </select>
            </div>

            {newItem.item === "Others" && (
              <div className="col-md-2">
                <label className="form-label">Others Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Others Name"
                  value={newItem.other_name}
                  onChange={(e) =>
                    setNewItem({ ...newItem, other_name: e.target.value })
                  }
                />
              </div>
            )}

            <div className="col-md-2">
              <label className="form-label">Category</label>
              <select 
                className="form-select" 
                value={newItem.category} 
                onChange={(e) => {
                  const selectedCategory = e.target.value;
                  setNewItem({ 
                    ...newItem, 
                    category: selectedCategory,
                    item_name: "",
                    item_name_tamil: ""
                  });
                }}
              >
                <option value="">Select Category</option>
                <option value="Gold">Gold</option>
                <option value="Silver">Silver</option>
                <option value="Gold Coin">Gold Coin</option>
                <option value="Silver Coin">Silver Coin</option>
              </select>
            </div>

            {/* Item Name dropdown - shows only items matching selected category */}
            <div className="col-md-2">
              <label className="form-label">Item Name</label>
              <div className="input-group">
                <select
                  className="form-select"
                  value={newItem.item_name || ""}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const selected = filteredProducts.find(p => 
                      (p.item_name_english || p.item_name) === selectedValue
                    );
                    setNewItem({
                      ...newItem,
                      item_name: selectedValue,
                      item_name_tamil: selected?.item_name_tamil || "",
                    });
                  }}
                  disabled={!newItem.category}
                >
                  <option value="">Select Item Name</option>
                  {filteredProducts.map((product) => {
                    const displayName = product.item_name_tamil 
                      ? `${product.item_name_english || product.item_name} / ${product.item_name_tamil}`
                      : (product.item_name_english || product.item_name);
                    return (
                      <option key={product.id} value={product.item_name_english || product.item_name}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={openProductModal}
                  title="Add New Product"
                  disabled={!newItem.category}
                >
                  <i className="bi bi-plus"></i>
                </button>
              </div>
              {!newItem.category && (
                <small className="text-muted">Please select category first</small>
              )}
            </div>

            <div className="col-md-1">
              <label className="form-label">Weight (g)</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                placeholder="Weight"
                value={newItem.weight}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setNewItem({ ...newItem, weight: value });
                  }
                }}
              />
            </div>

            {/* Adjustment Input */}
            <div className="col-md-2 position-relative">
              <label>Adjustment</label>
              <div className="input-group mt-2">
                <button
                  className="btn btn-outline-danger btn-sm"
                  type="button"
                  style={{ height: "30px" }}
                  onClick={() => {
                    const v = parseFloat(adjustmentInput) || 0;
                    if (v > 0) {
                      setNewItem((p) => ({
                        ...p,
                        adjustment: (parseFloat(p.adjustment || 0) - v).toFixed(3),
                      }));
                      setAdjustmentInput("");
                    }
                  }}
                >
                  <i className="fa-solid fa-minus"></i>
                </button>

                <input
                  type="text"
                  className="form-control form-control-sm text-center"
                  value={adjustmentInput}
                  placeholder="0.000"
                  onChange={(e) => {
                    if (/^\d*\.?\d*$/.test(e.target.value))
                      setAdjustmentInput(e.target.value);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      const v = parseFloat(adjustmentInput) || 0;
                      if (v > 0) {
                        setNewItem((p) => ({
                          ...p,
                          adjustment: (parseFloat(p.adjustment || 0) + v).toFixed(3),
                        }));
                        setAdjustmentInput("");
                      }
                    }
                  }}
                />

                <button
                  className="btn btn-outline-success btn-sm"
                  type="button"
                  style={{ height: "30px" }}
                  onClick={() => {
                    const v = parseFloat(adjustmentInput) || 0;
                    if (v > 0) {
                      setNewItem((p) => ({
                        ...p,
                        adjustment: (parseFloat(p.adjustment || 0) + v).toFixed(3),
                      }));
                      setAdjustmentInput("");
                    }
                  }}
                >
                  <i className="fa-solid fa-plus"></i>
                </button>

                {parseFloat(newItem.adjustment || 0) !== 0 && (
                  <button
                    className="btn btn-sm translate-middle-y me-1"
                    style={{
                      zIndex: 10,
                      background: "none",
                      border: "none",
                      position: "relative",
                      top: "10px",
                      right: "50px",
                    }}
                    onClick={() =>
                      setNewItem((p) => ({ ...p, adjustment: "0.000" }))
                    }
                  >
                    <i className="fa-solid fa-xmark text-danger"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-1">
              <label className="form-label">Purity (%)</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                placeholder="Purity"
                value={newItem.purity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d{0,3}(\.\d{0,2})?$/.test(value)) {
                    const numericValue = parseFloat(value);
                    if (
                      value === "" ||
                      (!isNaN(numericValue) && numericValue <= 100)
                    ) {
                      setNewItem({ ...newItem, purity: value });
                    } else {
                      toast.error("Purity cannot exceed 100%", 3000, "topRight");
                    }
                  }
                }}
              />
            </div>
            <div className="col-md-1">
              <label className="form-label">Pure (g)</label>
              <input
                type="text"
                className="form-control"
                value={newItem.pure || "0.000"}
                readOnly
              />
            </div>
            <div className="col-md-1">
              {editingItemId === null ? (
                <button
                  className="btn btn-success btn-sm mx-2 mt-4"
                  onClick={handleAddItem}
                >
                  <i className="bi bi-plus"></i>
                </button>
              ) : (
                <button
                  className="btn btn-info btn-sm mx-2 mt-4"
                  onClick={handleUpdateItem}
                >
                  <i className="bi bi-check"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="container-fluid p-2">
          <div className="row">
            <div className="col-lg-9">
              <div className="card card-body p-2">
                <h6>Item List</h6>
                <div className="p-2" style={{ backgroundColor: "#E8F0FA" }}>
                  <div className="table-container">
                    <table className="table table-layout-fixed w-100">
                      <thead className="bg-light">
                        <tr>
                          <th>S.No</th>
                          <th>Item</th>
                          <th>Item Name</th>
                          <th>Category</th>
                          <th>Weight(g)</th>
                          <th>Purity (%)</th>
                          <th>Pure(g)</th>
                          <th>Adjustment</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.length > 0 ? (
                          formData.items.map((item, index) => (
                            <tr key={item.id || index}>
                              <td>{index + 1}</td>
                              <td>
                                {item.item}
                                {item.other_name ? ` (${item.other_name})` : ""}
                              </td>
                              <td>
                                {item.item_name_tamil
                                  ? `${item.item_name} / ${item.item_name_tamil}`
                                  : item.item_name || "-"}
                              </td>
                              <td>{item.category}</td>
                              <td>{parseFloat(item.weight).toFixed(3)}g</td>
                              <td>{parseFloat(item.purity).toFixed(2)}%</td>
                              <td>{parseFloat(item.pure).toFixed(3)}g</td>
                              <td className="text-start">
                                <span
                                  className={`fw-bold ${
                                    parseFloat(item.adjustment) > 0
                                      ? "text-success"
                                      : parseFloat(item.adjustment) < 0
                                        ? "text-danger"
                                        : "text-muted"
                                  }`}
                                >
                                  {parseFloat(item.adjustment) > 0 ? "+" : ""}
                                  {parseFloat(item.adjustment || 0).toFixed(3)}g
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-light me-1"
                                  onClick={() => handleEditItem(item.id)}
                                >
                                  <i className="fa-solid fa-pen-to-square text-primary fs-6"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-light"
                                  onClick={() => handleRemoveItem(item.id)}
                                >
                                  <i className="fa-solid fa-trash text-danger fs-6"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center">
                              No items added yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {/* Supplier Payment History */}
              {selectedSupplier?.supplier_id && (
                <div className="card card-body p-2 mt-2">
                  <h6>
                    Payment History - {selectedSupplier.name}
                    {selectedSupplier.name_tamil && (
                      <span className="text-muted"> / {selectedSupplier.name_tamil}</span>
                    )}
                    ({selectedSupplier.supplier_id})
                  </h6>
                  <div className="p-2" style={{ backgroundColor: "#E8F0FA" }}>
                    <div className="table-container">
                      <table className="table table-layout-fixed w-100">
                        <thead className="bg-light">
                          <tr>
                            <th>Date</th>
                            <th>Total (g)</th>
                            <th>Paid</th>
                            <th>Balance (g)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingPaymentHistory ? (
                            <tr>
                              <td colSpan="4" className="text-center">
                                Loading...
                              </td>
                            </tr>
                          ) : supplierPaymentHistory.length > 0 ? (
                            supplierPaymentHistory.map((record) => (
                              <tr key={record.id || record.purchase_id}>
                                <td>{record.purchase_date}</td>
                                <td>
                                  {parseFloat(record.total_pure || 0).toFixed(3)}g
                                </td>
                                <td>
                                  {record.return_type === "Amount"
                                    ? `₹${parseFloat(record.paid || 0).toFixed(2)}`
                                    : record.return_type === "Pure"
                                      ? `${parseFloat(record.pure || 0).toFixed(3)}g`
                                      : "-"}
                                </td>
                                <td>
                                  {parseFloat(record.close || 0).toFixed(3)}g
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                No previous payment history for this supplier
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Section */}
            <div className="col-lg-3">
              <div className="card p-3">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Total Pure (g)</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.total_pure || "0.000"}
                      readOnly
                    />
                  </div>
                </div>

                {/* Return Type */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Return Type</label>
                  </div>
                  <div className="col-md-6">
                    <select
                      className="form-select"
                      value={formData.return_type}
                      onChange={(e) => {
                        const newReturnType = e.target.value;
                        setFormData({
                          ...formData,
                          return_type: newReturnType,
                          pure: "",
                          paid: "",
                          enter_amount: "",
                        });
                        setReturnPureCalculated("0.000");
                      }}
                    >
                      <option value="">Select Type</option>
                      <option value="Pure">Pure</option>
                      <option value="Amount">Amount</option>
                    </select>
                  </div>
                </div>

                {/* PURE MODE */}
                {formData.return_type === "Pure" && (
                  <div className="row mb-3 align-items-center">
                    <div className="col-md-3">
                      <label className="form-label fw-bold">Pure%</label>
                    </div>
                    <div className="col-md-3 mt-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="0.000"
                        value={formData.purePercent || ""}
                        readOnly
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-bold">Pure(g)</label>
                    </div>
                    <div className="col-md-3 mt-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="0.000"
                        value={formData.pure || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^\d*\.?\d*$/.test(val)) {
                            setFormData((prev) => ({ ...prev, pure: val }));
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* AMOUNT MODE */}
                {formData.return_type === "Amount" && (
                  <>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label className="form-label">Enter Amount (₹)</label>
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formData.paid || ""}
                          onChange={(e) => {
                            if (/^\d*\.?\d*$/.test(e.target.value)) {
                              setFormData((prev) => ({
                                ...prev,
                                paid: e.target.value,
                              }));
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div className="row mb-2">
                      <div className="col-6">
                        <label className="form-label fw-bold text-primary">
                          Return Pure (g)
                        </label>
                      </div>
                      <div className="col-6">
                        <input
                          type="text"
                          className="form-control form-control-sm bg-light"
                          value={returnPureCalculated}
                          readOnly
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Opening (g)</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.opening || "0.000"}
                      readOnly
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Current (g)</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.current || "0.000"}
                      readOnly
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Closing (g)</label>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.close || "0.000"}
                      readOnly
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between mt-2">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate("/purchases")}
                  >
                    <i className="fa-solid fa-arrow-left"></i> Back
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleClear}
                  >
                    <i className="bi bi-x-circle"></i> Clear
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowModal(true)}
                    disabled={loading}
                  >
                    <i className="bi bi-printer"></i> Print
                  </button>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    <i className="bi bi-save"></i> Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Confirmation Modal */}
      <div
        className={`modal fade ${showModal ? "show" : ""}`}
        style={{ display: showModal ? "block" : "none" }}
        tabIndex="-1"
        aria-labelledby="printModalLabel"
        aria-hidden={!showModal}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title text-primary" id="printModalLabel">
                Print Confirmation
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={() => setShowModal(false)}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="text-center">
                <p>Do you want to print the purchase bill?</p>
              </div>
              <div className="d-flex justify-content-center mt-4">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm mx-2"
                  onClick={() => navigate("/purchases")}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={closeModal}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Print"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && <div className="modal-backdrop fade show"></div>}

      {/* Add Supplier Modal with Tamil name support */}
      <div
        className={`modal fade ${showSupplierModal ? "show" : ""}`}
        style={{
          display: showSupplierModal ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
        aria-labelledby="addSupplierModalLabel"
        aria-hidden={!showSupplierModal}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6
                className="modal-title text-primary"
                id="addSupplierModalLabel"
              >
                Add Supplier
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeSupplierModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSupplierSubmit}>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="supplierId" className="form-label">
                        Supplier ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          supplierFormErrors.supplier_id ? "is-invalid" : ""
                        }`}
                        id="supplierId"
                        value={generateSupplierNumber}
                        disabled
                      />
                      {supplierFormErrors.supplier_id && (
                        <div className="invalid-feedback">
                          {supplierFormErrors.supplier_id}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="supplierName" className="form-label">
                        Name <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={`form-control ${
                            supplierFormErrors.name ? "is-invalid" : ""
                          }`}
                          id="supplierName"
                          placeholder="Enter Name (Tamil will auto-translate)"
                          value={supplierFormData.name}
                          onChange={(e) =>
                            setSupplierFormData({
                              ...supplierFormData,
                              name: e.target.value,
                              name_tamil: "",
                            })
                          }
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if (val && !supplierFormData.name_tamil) {
                              const tamil = await translateSupplierToTamil(val);
                              if (tamil) {
                                setSupplierFormData((prev) => ({
                                  ...prev,
                                  name_tamil: tamil,
                                }));
                                toast.info(`Tamil translation: ${tamil}`, 2000, "topRight");
                              }
                            }
                          }}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          style={{ height: "38px" }}
                          disabled={isTranslatingSupplier || !supplierFormData.name}
                          onClick={async () => {
                            const tamil = await translateSupplierToTamil(supplierFormData.name);
                            if (tamil) {
                              setSupplierFormData((prev) => ({
                                ...prev,
                                name_tamil: tamil,
                              }));
                              toast.success("Translated successfully!", 1500, "topRight");
                            } else {
                              toast.error("Translation failed. Please try again.", 2000, "topRight");
                            }
                          }}
                        >
                          {isTranslatingSupplier ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            "🌐 Translate"
                          )}
                        </button>
                      </div>
                      {supplierFormData.name_tamil && (
                        <small className="text-muted mt-1 d-block">
                          <strong>Tamil:</strong> {supplierFormData.name_tamil}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 text-muted"
                            onClick={() =>
                              setSupplierFormData((prev) => ({
                                ...prev,
                                name_tamil: "",
                              }))
                            }
                          >
                            ✕ clear
                          </button>
                        </small>
                      )}
                      {supplierFormErrors.name && (
                        <div className="invalid-feedback">
                          {supplierFormErrors.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="supplierMobile" className="form-label">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="10"
                        className={`form-control ${
                          supplierFormErrors.mobile ? "is-invalid" : ""
                        }`}
                        id="supplierMobile"
                        placeholder="Enter Mobile"
                        value={supplierFormData.mobile}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,10}$/.test(value)) {
                            setSupplierFormData({
                              ...supplierFormData,
                              mobile: value,
                            });
                          }
                        }}
                        required
                      />
                      {supplierFormErrors.mobile && (
                        <div className="invalid-feedback">
                          {supplierFormErrors.mobile}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="supplierAddress" className="form-label">
                        Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          supplierFormErrors.address ? "is-invalid" : ""
                        }`}
                        id="supplierAddress"
                        placeholder="Enter Address"
                        value={supplierFormData.address}
                        onChange={(e) =>
                          setSupplierFormData({
                            ...supplierFormData,
                            address: e.target.value,
                          })
                        }
                        required
                      />
                      {supplierFormErrors.address && (
                        <div className="invalid-feedback">
                          {supplierFormErrors.address}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="supplierStatus" className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${
                      supplierFormErrors.status ? "is-invalid" : ""
                    }`}
                    id="supplierStatus"
                    value={supplierFormData.status}
                    onChange={(e) =>
                      setSupplierFormData({
                        ...supplierFormData,
                        status: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  {supplierFormErrors.status && (
                    <div className="invalid-feedback">
                      {supplierFormErrors.status}
                    </div>
                  )}
                </div>
                <div className="text-center mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={loading}
                  >
                    {loading ? "Adding..." : "Add Supplier"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Add Product Modal with Tamil name support */}
      <div
        className={`modal fade ${showProductModal ? "show" : ""}`}
        style={{
          display: showProductModal ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
        aria-labelledby="addProductModalLabel"
        aria-hidden={!showProductModal}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title text-primary" id="addProductModalLabel">
                Add Product
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeProductModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleProductSubmit}>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="item_code" className="form-label">
                        Item Code
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="item_code"
                        value={productAddData.item_code}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="product_category" className="form-label">
                        Category <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select ${productFormErrors.category ? "is-invalid" : ""}`}
                        id="product_category"
                        value={productAddData.category}
                        onChange={(e) =>
                          setProductAddData({
                            ...productAddData,
                            category: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Category</option>
                        <option value="Gold">Gold</option>
                        <option value="Silver">Silver</option>
                        <option value="Gold Coin">Gold Coin</option>
                        <option value="Silver Coin">Silver Coin</option>
                      </select>
                      {productFormErrors.category && (
                        <div className="invalid-feedback">
                          {productFormErrors.category}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label htmlFor="item_name_english" className="form-label">
                        Item Name <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={`form-control ${productFormErrors.item_name ? "is-invalid" : ""}`}
                          id="item_name_english"
                          placeholder="Enter item name in English (Tamil will auto-translate)"
                          value={productAddData.item_name_english}
                          onChange={(e) =>
                            setProductAddData({
                              ...productAddData,
                              item_name_english: e.target.value,
                              item_name: e.target.value,
                              item_name_tamil: "",
                            })
                          }
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if (val && !productAddData.item_name_tamil) {
                              const tamil = await translateToTamil(val);
                              if (tamil) {
                                setProductAddData((prev) => ({
                                  ...prev,
                                  item_name_tamil: tamil,
                                }));
                                toast.info(`Tamil translation: ${tamil}`, 2000, "topRight");
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          title="Translate to Tamil"
                          style={{ height: "38px" }}
                          disabled={isTranslating || !productAddData.item_name_english}
                          onClick={async () => {
                            const tamil = await translateToTamil(productAddData.item_name_english);
                            if (tamil) {
                              setProductAddData((prev) => ({
                                ...prev,
                                item_name_tamil: tamil,
                              }));
                              toast.success("Translated successfully!", 1500, "topRight");
                            } else {
                              toast.error("Translation failed. Please try again.", 2000, "topRight");
                            }
                          }}
                        >
                          {isTranslating ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            "🌐 Translate"
                          )}
                        </button>
                      </div>
                      {productAddData.item_name_tamil && (
                        <small className="text-muted mt-1 d-block">
                          <strong>Tamil:</strong> {productAddData.item_name_tamil}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 text-muted"
                            onClick={() =>
                              setProductAddData((prev) => ({
                                ...prev,
                                item_name_tamil: "",
                              }))
                            }
                          >
                            ✕ clear
                          </button>
                        </small>
                      )}
                      {productFormErrors.item_name && (
                        <div className="invalid-feedback">
                          {productFormErrors.item_name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="pure_percent" className="form-label">
                        Purity (%) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        className={`form-control ${productFormErrors.pure_percent ? "is-invalid" : ""}`}
                        id="pure_percent"
                        placeholder="e.g. 99.9"
                        value={productAddData.pure_percent}
                        onChange={(e) =>
                          setProductAddData({
                            ...productAddData,
                            pure_percent: e.target.value,
                          })
                        }
                      />
                      {productFormErrors.pure_percent && (
                        <div className="invalid-feedback">
                          {productFormErrors.pure_percent}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label htmlFor="product_status" className="form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-select ${productFormErrors.status ? "is-invalid" : ""}`}
                        id="product_status"
                        value={productAddData.status}
                        onChange={(e) =>
                          setProductAddData({
                            ...productAddData,
                            status: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                      {productFormErrors.status && (
                        <div className="invalid-feedback">
                          {productFormErrors.status}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="mb-3">
                    <label htmlFor="product_image" className="form-label">
                      Image Upload
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      id="product_image"
                      onChange={(e) =>
                        setProductAddData({
                          ...productAddData,
                          image: e.target.files[0],
                        })
                      }
                      accept="image/*"
                    />
                  </div>
                </div>

                <div className="text-center mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={loading}
                  >
                    {loading ? "Adding..." : "Add Product"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {(showModal || showSupplierModal || showProductModal) && <div className="modal-backdrop fade show"></div>}
    </>
  );
}

export default AddPurchase;